/**
 * contentValidators/categorizeKeyChecks.ts
 *
 * Categorize sleutel-integriteit (observability check — Fase B).
 *
 * The TYPE stage validates categorize structure as a hard blocker, but the
 * C-S-K incident showed a categorize item can silently score wrong when its
 * `correctCategory` is missing or points outside `data.categories`. This
 * content-stage validator FLAGS such cases as `info` issues (severity low →
 * no gate impact) so the admin impact report can quantify how widespread the
 * problem is BEFORE any enforcement ramp ("observability before enforcement",
 * ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §FG4 / §F0.B).
 *
 * Cross-module: categorize is used by SPR, TAA, MET, … so this runs for every
 * categorize question regardless of module.
 *
 * Three failure shapes are detected (all `info`, discriminated via
 * `metadata.reason`):
 *  1. no_categories      — data.categories absent or empty
 *  2. missing_key        — an item has no usable correctCategory
 *  3. index_out_of_range — correctCategory is a number outside [0, n-1]
 */

import type { ValidationIssue } from '../../issueContract';
import { info } from '../../issueContract';
import type { Question } from '../../../../content';
import { STAGE } from './shared';

/** Info-severity for observability findings (no gate impact). */
const INFO_SEVERITY = 12;

/**
 * Pull the item list out of a categorize payload. The seeds use `data.items`
 * with `{ text, correctCategory }`; we read defensively so a renamed bucket
 * does not blind the check.
 */
function gatherItems(data: Record<string, unknown>): Record<string, unknown>[] {
  const raw = (data.items ?? data.cards ?? data.tokens) as unknown;
  if (!Array.isArray(raw)) return [];
  return raw.filter((e): e is Record<string, unknown> => !!e && typeof e === 'object');
}
export function validateCategorizeKeys(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (question.questionType !== 'categorize') return issues;
  const qId = question.id ?? 'UNKNOWN';
  const fId = question.familyId ?? 'UNKNOWN';
  const data = (question.data ?? {}) as Record<string, unknown>;
  const categories = data.categories;
  const categoryCount = Array.isArray(categories) ? categories.length : 0;

  // ── Shape 1: no categories at all ─────────────────────────────────────────
  if (categoryCount === 0) {
    issues.push(info('content', STAGE, 'CONTENT_CATEGORIZE_KEY_INVALID', 'Categorize question has no usable data.categories array — every item key is therefore unverifiable.', {
      field: 'data.categories',
      questionId: qId,
      familyId: fId,
      severity: INFO_SEVERITY,
      reason: 'no_categories',
      suggestion: 'Declare data.categories as a non-empty string array, then point each item.correctCategory at a valid index.'
    }));
    return issues;
  }
  const items = gatherItems(data);
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const key = item.correctCategory;
    const label = typeof item.text === 'string' ? item.text : typeof item.label === 'string' ? item.label : `item[${i}]`;

    // ── Shape 2: missing / non-numeric key ──────────────────────────────────
    if (typeof key !== 'number' || !Number.isInteger(key)) {
      issues.push(info('content', STAGE, 'CONTENT_CATEGORIZE_KEY_INVALID', `Categorize item "${label}" has no valid correctCategory (got ${JSON.stringify(key)}) — it will silently score against an undefined bucket.`, {
        field: `data.items[${i}].correctCategory`,
        questionId: qId,
        familyId: fId,
        severity: INFO_SEVERITY,
        reason: 'missing_key',
        suggestion: 'Add an integer correctCategory in [0, categories.length-1] to every item.'
      }));
      continue;
    }

    // ── Shape 3: out-of-range index ─────────────────────────────────────────
    if (key < 0 || key >= categoryCount) {
      issues.push(info('content', STAGE, 'CONTENT_CATEGORIZE_KEY_INVALID', `Categorize item "${label}" has correctCategory ${key}, outside the valid range [0, ${categoryCount - 1}] — the mapped category does not exist.`, {
        field: `data.items[${i}].correctCategory`,
        questionId: qId,
        familyId: fId,
        severity: INFO_SEVERITY,
        reason: 'index_out_of_range',
        expected: `[0, ${categoryCount - 1}]`,
        actual: String(key),
        suggestion: 'Repoint correctCategory at an existing category index, or add the missing category to data.categories.'
      }));
    }
  }
  return issues;
}